import { useEffect, useState } from 'react';
import { adminHostPerformance, adminListHosts } from '../../api/adminHosts';

const ranges = [30, 90, 365];

const AdminHostPerformance = ({ token }) => {
  const [hosts, setHosts] = useState([]);
  const [activeHost, setActiveHost] = useState('');
  const [range, setRange] = useState(30);
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const res = await adminListHosts(token, { limit: 100 });
        setHosts(res.data || []);
        if (res.data?.[0]) setActiveHost(res.data[0].id);
      } catch (err) {
        setError(err.message || 'Failed to load hosts');
      }
    })();
  }, [token]);

  useEffect(() => {
    if (!activeHost) return;
    (async () => {
      setLoading(true);
      setError('');
      try {
        const data = await adminHostPerformance(token, activeHost, range);
        setMetrics(data);
      } catch (err) {
        setError(err.message || 'Failed to load performance');
      } finally {
        setLoading(false);
      }
    })();
  }, [activeHost, range, token]);

  return (
    <div className="admin-card rounded-lg bg-white p-4 shadow-sm ring-1 ring-slate-200">
      <div className="flex items-center justify-between border-b border-slate-200 pb-3">
        <div>
          <p className="text-xs uppercase tracking-wide text-emerald-700">Hosts / Partners</p>
          <h1 className="admin-page-title text-xl font-bold text-slate-900">Host performance</h1>
          <p className="text-sm text-slate-600">Key metrics per host.</p>
        </div>
        <div className="flex gap-2">
          <select
            value={activeHost}
            onChange={(e) => setActiveHost(e.target.value)}
            className="rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none"
          >
            {hosts.map((h) => (
              <option key={h.id} value={h.id}>
                {h.full_name || h.phone}
              </option>
            ))}
          </select>
          <select
            value={range}
            onChange={(e) => setRange(Number(e.target.value))}
            className="rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none"
          >
            {ranges.map((r) => (
              <option key={r} value={r}>
                Last {r} days
              </option>
            ))}
          </select>
        </div>
      </div>

      {error && <div className="mt-3 rounded-lg border border-rose-100 bg-rose-50 px-3 py-2 text-sm text-rose-700">{error}</div>}

      {loading && <div className="mt-3 text-sm text-slate-500">Loading...</div>}

      {metrics && (
        <div className="mt-4 grid gap-3 md:grid-cols-3">
          <div className="rounded-lg border border-slate-200 p-3">
            <p className="text-sm text-slate-600">Total properties</p>
            <p className="text-2xl font-semibold text-slate-900">{metrics.total_properties}</p>
          </div>
          <div className="rounded-lg border border-slate-200 p-3">
            <p className="text-sm text-slate-600">Active properties</p>
            <p className="text-2xl font-semibold text-slate-900">{metrics.active_properties}</p>
          </div>
          <div className="rounded-lg border border-slate-200 p-3">
            <p className="text-sm text-slate-600">Revenue (range)</p>
            <p className="text-2xl font-semibold text-slate-900">INR {Number(metrics.revenue_total || 0).toLocaleString()}</p>
          </div>
          <div className="rounded-lg border border-slate-200 p-3">
            <p className="text-sm text-slate-600">Bookings</p>
            <p className="text-2xl font-semibold text-slate-900">{metrics.bookings}</p>
          </div>
          <div className="rounded-lg border border-slate-200 p-3">
            <p className="text-sm text-slate-600">Cancellations</p>
            <p className="text-2xl font-semibold text-slate-900">{metrics.cancellations}</p>
          </div>
          <div className="rounded-lg border border-slate-200 p-3">
            <p className="text-sm text-slate-600">Reviews</p>
            <p className="text-2xl font-semibold text-slate-900">
              {Number(metrics.reviews_avg || 0).toFixed(1)} ({metrics.reviews_count})
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminHostPerformance;
